import { useCallback, useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import { Send, Trash2, Webhook } from "lucide-react";

import { ConfirmDialog } from "~/components/ConfirmDialog";
import { TextInput } from "~/components/CreateRoomDialog";
import { GhostButton, PrimaryButton } from "~/components/MessageButtons";
import { Section } from "~/components/SettingsSection";
import { useMatrix } from "~/context/MatrixContext";
import { getHookshotBotUserId, getHookshotWidgetUrl } from "~/env";
import {
  GENERIC_HOOK_EVENT_TYPE,
  HookshotClient,
  HookshotError,
  isHttpsUrl,
  type Hook,
  type HookKind,
} from "~/lib/hookshot";
import {
  canSendStateEvent,
  getMembers,
  inviteUser,
  requestOpenIdToken,
  subscribeRooms,
  type Client,
  type RoomMember,
  type RoomSummary,
} from "~/lib/matrix";
import { ICON_STROKE } from "~/lib/icon";

const ENABLE_POLL_MS = 1000;
const ENABLE_TIMEOUT_MS = 15000;

export interface IntegrationsSectionProps {
  room: RoomSummary;
}

export function IntegrationsSection({ room }: IntegrationsSectionProps) {
  const { client, userId } = useMatrix();
  const baseUrl = getHookshotWidgetUrl();
  const botUserId = getHookshotBotUserId();
  if (!client || !userId || !baseUrl || !botUserId || room.isDirect || room.isSpace) return null;
  return (
    <Integrations
      client={client}
      userId={userId}
      room={room}
      baseUrl={baseUrl}
      botUserId={botUserId}
    />
  );
}

function botHasJoined(members: RoomMember[], botUserId: string): boolean {
  return members.some(
    (member) =>
      member.userId === botUserId &&
      (member.membership === undefined || member.membership === "join"),
  );
}

function errorMessage(err: unknown): string {
  if (err instanceof HookshotError) {
    if (err.kind === "forbidden") return "You don't have permission to manage integrations here.";
    if (err.kind === "not_in_room") return "Integrations are off for this channel.";
    if (err.kind === "invalid_url") return "Use an https:// URL.";
  }
  return "Couldn't reach integrations. Try again.";
}

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function Integrations({
  client,
  userId,
  room,
  baseUrl,
  botUserId,
}: {
  client: Client;
  userId: string;
  room: RoomSummary;
  baseUrl: string;
  botUserId: string;
}) {
  const hookshot = useMemo(
    () => new HookshotClient({ baseUrl, getOpenIdToken: () => requestOpenIdToken(client) }),
    [client, baseUrl],
  );
  const canManage = canSendStateEvent(client, room.id, userId, GENERIC_HOOK_EVENT_TYPE);
  const [botJoined, setBotJoined] = useState(() =>
    botHasJoined(getMembers(client, room.id), botUserId),
  );
  const [enabling, setEnabling] = useState(false);
  const [enableError, setEnableError] = useState<string | null>(null);
  const [hooks, setHooks] = useState<Hook[] | null>(null);
  const [listError, setListError] = useState<string | null>(null);
  const [revealed, setRevealed] = useState<Hook | null>(null);
  const [pendingDelete, setPendingDelete] = useState<Hook | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const mountedRef = useRef(true);
  const reportedAbsentRef = useRef(false);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(
    () =>
      subscribeRooms(client, () => {
        if (reportedAbsentRef.current) return;
        if (botHasJoined(getMembers(client, room.id), botUserId)) setBotJoined(true);
      }),
    [client, room.id, botUserId],
  );

  useEffect(() => {
    if (!botJoined) return;
    let current = true;
    hookshot.listHooks(room.id).then(
      (next) => {
        if (!current) return;
        setHooks(next);
        setListError(null);
      },
      (err: unknown) => {
        if (!current) return;
        if (err instanceof HookshotError && err.kind === "not_in_room") {
          reportedAbsentRef.current = true;
          setBotJoined(false);
          return;
        }
        setListError(errorMessage(err));
      },
    );
    return () => {
      current = false;
    };
  }, [botJoined, hookshot, room.id]);

  const enable = useCallback(async () => {
    setEnabling(true);
    setEnableError(null);
    reportedAbsentRef.current = false;
    try {
      await inviteUser(client, room.id, botUserId);
      for (let waited = 0; waited < ENABLE_TIMEOUT_MS; waited += ENABLE_POLL_MS) {
        await delay(ENABLE_POLL_MS);
        if (!mountedRef.current) return;
        if (botHasJoined(getMembers(client, room.id), botUserId)) {
          setBotJoined(true);
          return;
        }
      }
      setEnableError("Couldn't enable integrations. Try again.");
    } catch {
      if (mountedRef.current) setEnableError("Couldn't enable integrations. Try again.");
    } finally {
      if (mountedRef.current) setEnabling(false);
    }
  }, [client, room.id, botUserId]);

  const added = useCallback((hook: Hook) => {
    setHooks((prev) => [...(prev ?? []), hook]);
    if (hook.kind === "incoming") setRevealed(hook);
  }, []);

  const confirmDelete = useCallback(async () => {
    const hook = pendingDelete;
    setPendingDelete(null);
    if (!hook) return;
    setActionError(null);
    try {
      await hookshot.deleteHook(room.id, hook.id);
      setHooks((prev) => (prev ?? []).filter((item) => item.id !== hook.id));
      setRevealed((prev) => (prev?.id === hook.id ? null : prev));
    } catch (err) {
      setActionError(errorMessage(err));
    }
  }, [hookshot, pendingDelete, room.id]);

  return (
    <Section title="Integrations">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-3)" }}>
        {!botJoined ? (
          <>
            <BodyText>Integrations are off for this channel</BodyText>
            {canManage && (
              <div>
                <PrimaryButton onClick={() => void enable()} disabled={enabling}>
                  {enabling ? "Enabling…" : "Enable integrations"}
                </PrimaryButton>
              </div>
            )}
            {enableError && <InlineError>{enableError}</InlineError>}
          </>
        ) : (
          <>
            {hooks === null && !listError && <BodyText muted>Loading…</BodyText>}
            {listError && <InlineError>{listError}</InlineError>}
            {hooks && hooks.length === 0 && <BodyText muted>No webhooks yet.</BodyText>}
            {hooks && hooks.length > 0 && (
              <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                {hooks.map((hook) => (
                  <HookRow
                    key={hook.id}
                    hook={hook}
                    canManage={canManage}
                    onDelete={() => setPendingDelete(hook)}
                  />
                ))}
              </ul>
            )}
            {revealed?.url && (
              <RevealedUrl
                url={revealed.url}
                roomName={room.name}
                onDone={() => setRevealed(null)}
              />
            )}
            {actionError && <InlineError>{actionError}</InlineError>}
            {canManage && hooks !== null && (
              <AddHook hookshot={hookshot} roomId={room.id} onAdded={added} />
            )}
          </>
        )}
      </div>
      {pendingDelete && (
        <ConfirmDialog
          title="Delete webhook"
          body={`Delete "${pendingDelete.name}"? Anything posting to it will stop working.`}
          confirmLabel="Delete"
          onCancel={() => setPendingDelete(null)}
          onConfirm={() => void confirmDelete()}
        />
      )}
    </Section>
  );
}

const KIND_LABEL: Record<HookKind, string> = {
  incoming: "Incoming",
  outgoing: "Outgoing",
};

function HookRow({
  hook,
  canManage,
  onDelete,
}: {
  hook: Hook;
  canManage: boolean;
  onDelete: () => void;
}) {
  const Icon = hook.kind === "incoming" ? Webhook : Send;
  return (
    <li
      data-hook-id={hook.id}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--chat-space-3)",
        minHeight: 40,
      }}
    >
      <Icon
        aria-hidden
        size={16}
        strokeWidth={ICON_STROKE}
        style={{ color: "var(--chat-fg-secondary)", flexShrink: 0 }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-md-size)",
            lineHeight: "var(--chat-type-body-md-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {hook.name || "Untitled"}
        </div>
        <div
          style={{
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-xs-size)",
            lineHeight: "var(--chat-type-body-xs-line)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {KIND_LABEL[hook.kind]}
          {canManage && hook.kind === "outgoing" && hook.url ? ` · ${hook.url}` : ""}
        </div>
      </div>
      {canManage && (
        <button
          type="button"
          onClick={onDelete}
          aria-label={`Delete ${hook.name || "webhook"}`}
          className="chat-button"
          style={{
            width: 32,
            height: 32,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            border: "none",
            borderRadius: "var(--chat-radius-sm)",
            background: "transparent",
            color: "var(--chat-fg-secondary)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <Trash2 size={14} strokeWidth={ICON_STROKE} />
        </button>
      )}
    </li>
  );
}

function RevealedUrl({
  url,
  roomName,
  onDone,
}: {
  url: string;
  roomName: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const copy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setCopyFailed(false);
    } catch {
      setCopied(false);
      setCopyFailed(true);
    }
  }, [url]);
  const curl = `curl -X POST -H 'Content-Type: application/json' -d '{"text":"Hello from CI"}' ${url}`;
  return (
    <div
      data-testid="webhook-url-reveal"
      style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-2)" }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: "var(--chat-space-2)" }}>
        <input
          readOnly
          aria-label="Webhook URL"
          value={url}
          onFocus={(e) => e.currentTarget.select()}
          style={{
            flex: 1,
            minWidth: 0,
            height: 32,
            padding: 0,
            border: "none",
            borderBottom: "1px solid var(--chat-input-border)",
            background: "transparent",
            color: "var(--chat-fg-primary)",
            fontFamily: "var(--chat-font-mono)",
            fontSize: "var(--chat-type-body-xs-size)",
            outline: "none",
          }}
        />
        <GhostButton onClick={() => void copy()}>{copied ? "Copied" : "Copy"}</GhostButton>
      </div>
      {copyFailed && <InlineError>Couldn't copy. Select the URL instead.</InlineError>}
      <BodyText muted small>
        {`Anyone with this URL can post to #${roomName}. Treat it like a password.`}
      </BodyText>
      <code
        style={{
          display: "block",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-mono)",
          fontSize: "var(--chat-type-body-xs-size)",
          lineHeight: "var(--chat-type-body-xs-line)",
          wordBreak: "break-all",
        }}
      >
        {curl}
      </code>
      <div>
        <GhostButton onClick={onDone}>Done</GhostButton>
      </div>
    </div>
  );
}

function AddHook({
  hookshot,
  roomId,
  onAdded,
}: {
  hookshot: HookshotClient;
  roomId: string;
  onAdded: (hook: Hook) => void;
}) {
  const [mode, setMode] = useState<HookKind | null>(null);
  const [name, setName] = useState("");
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameId = `${useId()}-hook-name`;
  const urlId = `${useId()}-hook-url`;

  const open = (next: HookKind) => {
    setMode(next);
    setName("");
    setUrl("");
    setError(null);
  };

  const submit = async () => {
    if (!mode || busy) return;
    const trimmedName = name.trim();
    const trimmedUrl = url.trim();
    if (!trimmedName) {
      setError("Give the webhook a name.");
      return;
    }
    if (mode === "outgoing" && !isHttpsUrl(trimmedUrl)) {
      setError("Use an https:// URL.");
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const hook =
        mode === "incoming"
          ? await hookshot.createIncoming(roomId, trimmedName)
          : await hookshot.createOutgoing(roomId, trimmedName, trimmedUrl);
      onAdded(hook);
      setMode(null);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!mode) {
    return (
      <div style={{ display: "flex", flexWrap: "wrap", gap: "var(--chat-space-2)" }}>
        <GhostButton onClick={() => open("incoming")}>Add incoming webhook</GhostButton>
        <GhostButton onClick={() => open("outgoing")}>Add outgoing webhook</GhostButton>
      </div>
    );
  }

  return (
    <form
      aria-label={mode === "incoming" ? "Add incoming webhook" : "Add outgoing webhook"}
      onSubmit={(e) => {
        e.preventDefault();
        void submit();
      }}
      style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-3)" }}
    >
      <FieldLabel id={nameId} label="Name">
        <TextInput id={nameId} value={name} onChange={setName} placeholder="CI" />
      </FieldLabel>
      {mode === "outgoing" && (
        <FieldLabel id={urlId} label="HTTPS URL">
          <TextInput
            id={urlId}
            value={url}
            onChange={setUrl}
            placeholder="https://example.com/hooks/matrix"
          />
        </FieldLabel>
      )}
      {error && <InlineError>{error}</InlineError>}
      <div style={{ display: "flex", gap: "var(--chat-space-2)" }}>
        <PrimaryButton onClick={() => void submit()} disabled={busy}>
          {busy ? "Creating…" : "Create"}
        </PrimaryButton>
        <GhostButton onClick={() => setMode(null)} disabled={busy}>
          Cancel
        </GhostButton>
      </div>
    </form>
  );
}

function FieldLabel({ id, label, children }: { id: string; label: string; children: ReactNode }) {
  return (
    <div>
      <label
        htmlFor={id}
        style={{
          display: "block",
          marginBottom: "var(--chat-space-1)",
          color: "var(--chat-fg-secondary)",
          fontFamily: "var(--chat-font-sans)",
          fontSize: "var(--chat-type-body-sm-size)",
          lineHeight: "var(--chat-type-body-sm-line)",
        }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function BodyText({
  children,
  muted,
  small,
}: {
  children: ReactNode;
  muted?: boolean;
  small?: boolean;
}) {
  return (
    <p
      style={{
        margin: 0,
        color: muted ? "var(--chat-fg-secondary)" : "var(--chat-fg-primary)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: small ? "var(--chat-type-body-xs-size)" : "var(--chat-type-body-sm-size)",
        lineHeight: small ? "var(--chat-type-body-xs-line)" : "var(--chat-type-body-sm-line)",
      }}
    >
      {children}
    </p>
  );
}

function InlineError({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      style={{
        margin: 0,
        color: "var(--chat-state-danger)",
        fontFamily: "var(--chat-font-sans)",
        fontSize: "var(--chat-type-body-xs-size)",
        lineHeight: "var(--chat-type-body-xs-line)",
      }}
    >
      {children}
    </p>
  );
}
